/* eslint-disable no-nested-ternary */
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography, Stack, TextField } from '@mui/material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { navigationSettingsState, sliderValueState } from 'store';
import { FilterMenu } from './components/FilterMenu';
import { SortMenu } from './components/SortMenu';
import { FinishModal } from './components/FinishModal';
import { RatingBoard } from './components/RatingBoard';
import { Tour, TourType } from '@/_models/Tour';
import { createInputProps, renderIcon } from './util';
import DeviceManager from '@/components/util/deviceManager';
import { NavigationCounterWrapper } from './components/NavigationCounterWrapper';

const outOfWrapperStype = {
  position: 'absolute',
  top: { xs: 43, lg: 80 },
  left: 0,
  right: 0,
  mx: 'auto',
};

type NavigationSearchBarProps = {
  setDestination: (location: any, navigationSettings: any, setSliderValue: any) => void;
  menuItems: any[];
  isBackgroundVisible?: boolean;
  setBackgroundVisible?: (val: boolean) => void;
  actionPathFinished?: boolean;
  tour: Tour;
  target: any;
  categories: any[];
  moveToStartingPoint: () => Promise<void>;
  filtersEnabled: boolean;
  sortingEnabled: boolean;
  defaultSorting: string;
  navigationFinishScreen: any;
  tourType: TourType;
  selectedCategory: any;
  selectCategory: (category: any) => void;
  appState?: string;
  setActionPathFinished: (val: boolean) => void;
  isSearchBarFocused: boolean;
  setIsSearchBarFocused: Dispatch<SetStateAction<boolean>>;
  rtl: boolean;
  inAutoNavigate?: boolean;
};

export const NavigationSearchBar = ({
  setDestination,
  menuItems,
  isBackgroundVisible,
  setBackgroundVisible,
  actionPathFinished,
  tour,
  target,
  categories,
  moveToStartingPoint,
  filtersEnabled,
  sortingEnabled,
  defaultSorting,
  navigationFinishScreen,
  tourType,
  selectedCategory,
  selectCategory,
  appState,
  setActionPathFinished,
  isSearchBarFocused,
  setIsSearchBarFocused,
  rtl,
  inAutoNavigate,
}: NavigationSearchBarProps) => {
  const { sharingEnabled, popupTitle, qrEnabled, qrTitle, smsEnabled, smsTitle, smsText } = navigationFinishScreen;
  const inputRef = useRef<HTMLInputElement>(null);
  const ratingBoardRef = useRef(null);
  const [inputClientRect, setInputClientRect] = useState(null);
  const [ratingClientRect, setRatingClientRect] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isRatingBoardVisible, setIsRatingBoardVisible] = useState(false);
  const [loadingFinished, setLoadingFinished] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<number[]>([]);
  const [sortingValue, setSortingValue] = useState('default');
  const [kioskIsReset, setKioskIsReset] = useState(false);
  const navigationSettings = useRecoilValue(navigationSettingsState);
  const setSliderValue = useSetRecoilState(sliderValueState);

  const filteredItems = useMemo(
    () =>
      menuItems?.filter((item) => {
        if (selectedCategory) {
          return item.categoryId === selectedCategory.id && item[item.type]?.title.toLowerCase().includes(searchText.toLowerCase());
        }
        if (selectedFilters.length) {
          return item[item.type]?.title.toLowerCase().includes(searchText.toLowerCase()) && !!item?.filters?.find((filter) => selectedFilters.includes(filter.id));
        }
        return (
          item[item.type]?.title.toLowerCase().includes(searchText.toLowerCase()) ||
          !!item?.filters?.find((filter) => filter?.title?.toLowerCase().includes(searchText.toLowerCase()))
        );
      }),
    [menuItems, searchText, selectedCategory, selectedFilters],
  );

  const wrapperWidth = useMemo(() => {
    if (DeviceManager.isMobile()) {
      return '100%';
    }
    if (!isBackgroundVisible) {
      return '750px';
    }
    if (categories.length <= 5) {
      return '810px';
    }
    return '1138px';
  }, [isBackgroundVisible, categories]);

  const handleItemSelect = async (item: any) => {
    setIsSearchBarFocused(false);
    setShowModal(false);
    setSearchText(item[item.type].title);
    setIsRatingBoardVisible(false);
    setActionPathFinished(false);
    setLoadingFinished(false);
    setSliderValue(0);

    if (tourType === TourType.Kiosk) {
      setKioskIsReset(false);
      moveToStartingPoint().then(() => {
        setDestination({ sid: item?.tagSid }, navigationSettings, setSliderValue);
      });
    } else {
      setDestination({ sid: item?.tagSid }, navigationSettings, setSliderValue);
    }
  };

  const resetNavigation = (caller?: string) => {
    setSearchText('');
    setShowModal(false);
    setIsRatingBoardVisible(false);
    setBackgroundVisible(true);
    setIsSearchBarFocused(true);
    moveToStartingPoint();
    if (caller === 'fromKioskTimer') {
      setKioskIsReset(true);
      selectCategory(null);
    }
  };

  const clearSearch = () => {
    setSearchText('');
    if (categories.length > 1) {
      selectCategory(null);
    }
    setBackgroundVisible(true);
    setShowModal(false);
    setIsRatingBoardVisible(false);
  };

  useEffect(() => {
    if (defaultSorting) {
      setSortingValue(defaultSorting);
    }
  }, [defaultSorting]);

  useEffect(() => {
    if (isRatingBoardVisible) {
      setRatingClientRect(ratingBoardRef.current.getBoundingClientRect());
    }
  }, [isRatingBoardVisible]);

  useEffect(() => {
    if (inputRef.current) {
      setInputClientRect(inputRef.current.getBoundingClientRect());
    }
  }, [isBackgroundVisible]);

  useEffect(() => {
    // Check if the search bar should be focused and if the ref is attached to an element
    if (isSearchBarFocused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchBarFocused]);

  useEffect(() => {
    if (isBackgroundVisible) {
      setShowModal(false);
      setIsRatingBoardVisible(false);
    }
  }, [isBackgroundVisible]);

  useEffect(() => {
    if (loadingFinished && tourType === TourType.Kiosk) {
      setShowModal(true);
    }
    if (loadingFinished && (tourType === TourType.Regular || (tourType === TourType.Kiosk && !sharingEnabled))) {
      resetNavigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFinished, tourType, sharingEnabled]);

  useEffect(() => {
    if (selectedCategory || categories.length === 1) {
      setIsSearchBarFocused(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, categories]);

  useEffect(() => {
    if (actionPathFinished) {
      setIsRatingBoardVisible(true);
    }
  }, [actionPathFinished]);

  return (
    <>
      <Stack
        sx={{
          width: { xs: `calc(${wrapperWidth} - 28px)`, lg: isSearchBarFocused ? `calc(${wrapperWidth} + 30px)` : wrapperWidth },
          mb: '17px',
          ...(!isBackgroundVisible && outOfWrapperStype),
          transition: 'width 0.3s ease-in-out',
          pointerEvents: 'auto',
        }}
      >
        {!!isBackgroundVisible && (
          <Stack width="100%" direction="row" alignItems="flex-start" justifyContent="center" mt={{ xs: '30px', lg: '79px' }}>
            {!!target && !kioskIsReset && (
              <Button
                sx={{
                  minWidth: 'unset',
                  width: '44px',
                  height: '44px',
                  background: '#fff',
                  borderRadius: '8px',
                  display: { xs: 'none', lg: 'inline-flex' },
                  '&:hover': {
                    background: '#fff',
                  },
                }}
                variant="contained"
                onClick={() => {
                  setSearchText('');
                  if (categories?.length > 1) selectCategory(null);
                  setIsSearchBarFocused(true);
                }}
              >
                <i
                  className="fa-solid fa-bars"
                  style={{
                    color: '#000',
                  }}
                />
              </Button>
            )}
            <Typography fontSize={{ xs: '20px', lg: '32px' }} mx="auto" fontWeight={{ xs: 500, lg: 600 }} height="48px">
              {navigationSettings?.titleEnabled && navigationSettings?.title ? navigationSettings?.title : ''}
            </Typography>
            {!!target && !kioskIsReset && (
              <Button
                sx={{
                  minWidth: 'unset',
                  width: '44px',
                  height: '44px',
                  background: '#fff',
                  borderRadius: '8px',
                  display: { xs: 'none', lg: 'inline-flex' },
                  '&:hover': {
                    background: '#fff',
                  },
                }}
                variant="contained"
                onClick={() => setBackgroundVisible(false)}
              >
                <i
                  className="fa-solid fa-xmark"
                  style={{
                    color: '#000',
                  }}
                />
              </Button>
            )}
          </Stack>
        )}
        <TextField
          disabled={appState !== 'playing'}
          autoComplete="off"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          ref={inputRef}
          InputProps={createInputProps({
            isBackgroundVisible,
            clearSearch,
            isSearchBarFocused,
            selectedCategory,
            categories,
            selectCategory,
            searchText,
            actionPathFinished,
            loadingFinished,
            rtl,
          })}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            fieldset: { height: '100%', top: 0, border: 'none' },
            mb: '17px',
            mt: '21px',
            pointerEvents: 'auto',
          }}
          placeholder={rtl ? 'מה לחפש?' : 'What are you searching for?'}
          onFocus={() => {
            if (!isBackgroundVisible) {
              setBackgroundVisible(true);
            }
            setIsSearchBarFocused(true);
          }}
        />
      </Stack>
      {(!!searchText.length || selectedCategory || categories.length === 1) && isSearchBarFocused && isBackgroundVisible && appState === 'playing' && (
        <Stack
          width={{ xs: wrapperWidth, lg: isSearchBarFocused ? `calc(${wrapperWidth} + 30px)` : wrapperWidth }}
          flexWrap="wrap"
          sx={{
            zIndex: 1000,
            position: 'absolute',
            left: 0,
            right: 0,
            top: inputClientRect?.top + inputClientRect?.height,
            mx: 'auto',
            mt: '10px',
            borderRadius: '8px',
            background: '#fff',
            border: { xs: 'none', lg: '1px solid #D0D5DD' },
            animation: 'fade-in 0.5s ease-in-out forwards',
            '@keyframes fade-in': {
              '0%': {
                opacity: 0,
              },
              '100%': {
                opacity: 1,
              },
            },
          }}
        >
          <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center" height="72px" px="20px">
            <Typography fontSize="16px" fontWeight={500}>
              {categories.length === 1 && !searchText ? (rtl ? 'מיקומים' : 'Locations') : rtl ? 'תוצאות' : 'Results'}
            </Typography>
            <Stack direction="row" gap={1}>
              {filtersEnabled && <FilterMenu rtl={rtl} menuItems={menuItems} selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />}
              {sortingEnabled && <SortMenu rtl={rtl} sortingValue={sortingValue} setSortingValue={setSortingValue} />}
            </Stack>
          </Stack>
          <Stack
            sx={{
              width: '100%',
              flexDirection: 'row',
              flexWrap: 'wrap',
              maxHeight: '410px',
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'none',
            }}
          >
            {filteredItems.map((item) => {
              return (
                <Stack
                  key={item.id}
                  width="100%"
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  height="82px"
                  px="20px"
                  sx={{
                    borderTop: { xs: 'none', lg: '1px solid #D0D5DD' },
                    cursor: 'pointer',
                    '&:hover': {
                      background: 'rgba(32, 85, 255, 0.06)',
                    },
                  }}
                  onClick={() => handleItemSelect(item)}
                >
                  <Stack direction="row" alignItems="center">
                    <Stack
                      width="38px"
                      height="38px"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      sx={{ background: 'transparent', borderRadius: '8px', p: 0, mr: rtl ? '0' : '14px', ml: rtl ? '14px' : '0', border: '1px solid #E8E8E8' }}
                    >
                      {renderIcon(item[item.type], null, null)}
                    </Stack>
                    <Stack>
                      <Typography fontSize="16px" fontWeight={500}>
                        {item[item.type]?.title}
                      </Typography>
                      <Typography fontSize="14px" fontWeight={400}>
                        {item[item.type]?.description}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack
                    sx={{
                      display: { xs: 'none', lg: 'flex' },
                      ':hover': {
                        background: 'rgba(32, 85, 255, 0.06)',
                      },
                    }}
                  >
                    <Typography textAlign="center">{item?.category?.title}</Typography>
                  </Stack>

                  <Stack direction="row" gap="10px" alignItems="center" width="128px" justifyContent="flex-end">
                    {/* TODO calc distance if possible */}
                    {/* <Typography fontSize="16px" fontWeight={400} sx={{ color: '#2055FF' }}>
                      {item?.distance}m to go
                    </Typography> */}
                    <i className={`fa-solid fa-chevron-${rtl ? 'left' : 'right'}`} />
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
      {isRatingBoardVisible && !isBackgroundVisible && (
        <RatingBoard
          ratingBoardRef={ratingBoardRef}
          ratingClientRect={ratingClientRect}
          setRatingClientRect={setRatingClientRect}
          ratingEnabled={navigationFinishScreen.ratingEnabled}
          beforeVoteText={navigationFinishScreen.beforeVoteText}
          afterVoteText={navigationFinishScreen.afterVoteText}
          inputClientRect={inputClientRect}
          actionPathFinished={actionPathFinished}
          loadingFinished={loadingFinished}
          setLoadingFinished={setLoadingFinished}
          tour={tour}
          target={target}
          rtl={rtl}
        />
      )}
      {showModal && sharingEnabled && tourType === TourType.Kiosk && !isBackgroundVisible && (
        <FinishModal
          isOpen={showModal}
          inputClientRect={inputClientRect}
          ratingClientRect={ratingClientRect}
          target={target}
          popupTitle={popupTitle}
          qrEnabled={qrEnabled}
          qrTitle={qrTitle}
          smsEnabled={smsEnabled}
          smsTitle={smsTitle}
          smsText={smsText}
          rtl={rtl}
        />
      )}
      {tourType === TourType.Kiosk && <NavigationCounterWrapper resetNavigation={resetNavigation} isBackgroundVisible={isBackgroundVisible} inAutoNavigate={inAutoNavigate} />}
    </>
  );
};
